import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { useContent, useContentRefresh } from "../content-context";
import { chunkedUpload, isVideoFile } from "../utils/mediaUpload";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PageVideoCard({
  contentKey,
  title,
  blurb,
  headingDefault,
  eyebrowDefault,
  testPrefix,
}) {
  const content = useContent();
  const refresh = useContentRefresh();
  const video = content ? content[contentKey] : null;
  const [busy, setBusy] = useState("");
  const [pct, setPct] = useState(0);
  const [heading, setHeading] = useState(null);
  const [caption, setCaption] = useState(null);
  const videoRef = useRef(null);
  const posterRef = useRef(null);

  const saveVideo = async (updated) => {
    const res = await fetch(`${API}/api/admin/content`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ content: { ...content, [contentKey]: updated } }),
    });
    if (!res.ok) throw new Error("save failed");
    await refresh();
  };

  const onVideo = async (file) => {
    if (!file) return;
    if (!isVideoFile(file)) {
      toast.error("Please choose a video file — MP4, WebM or MOV, up to 500MB.");
      return;
    }
    setBusy("video");
    setPct(0);
    try {
      const done = await chunkedUpload(API, file, setPct);
      await saveVideo({
        ...(video || {}),
        src: done.url,
        uploadDate: new Date().toISOString().slice(0, 10),
      });
      toast.success("Video is live — the page is updated.");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy("");
      setPct(0);
    }
  };

  const onPoster = async (file) => {
    if (!file) return;
    setBusy("poster");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
      await saveVideo({ ...(video || {}), poster: data.url });
      toast.success("Cover image updated.");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    setBusy("remove");
    try {
      const copy = { ...content };
      delete copy[contentKey];
      const res = await fetch(`${API}/api/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: copy }),
      });
      if (!res.ok) throw new Error("remove failed");
      await refresh();
      toast.success("Video removed from the page.");
    } catch {
      toast.error("Could not remove. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const saveDetails = async () => {
    setBusy("details");
    try {
      await saveVideo({
        ...(video || {}),
        ...(heading !== null ? { heading } : {}),
        ...(caption !== null ? { caption } : {}),
      });
      toast.success("Details saved.");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-8 border border-ivory/15 bg-navy-2 p-8 md:p-10" data-testid={`${testPrefix}-card`}>
      <h2 className="font-display text-3xl text-ivory">{title}</h2>
      <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-ivory/70">{blurb}</p>

      {video && video.src && (
        <div className="mt-6 flex flex-wrap items-start gap-5 border border-ivory/15 bg-navy p-4">
          <video
            src={video.src}
            poster={video.poster || undefined}
            preload="metadata"
            className="max-h-40 w-auto bg-black"
            data-testid={`${testPrefix}-current`}
          />
          <div className="min-w-[220px] flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">
              {video.eyebrow || eyebrowDefault}
            </p>
            <p className="mt-1 font-display text-xl text-ivory">{video.heading || headingDefault}</p>
            <button
              onClick={remove}
              disabled={!!busy}
              data-testid={`${testPrefix}-remove`}
              className="mt-3 flex items-center gap-2 border border-red-400/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-red-300 transition-colors hover:bg-red-400/10 disabled:opacity-60"
            >
              {busy === "remove" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove video
            </button>
          </div>
        </div>
      )}

      <input
        ref={videoRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-quicktime"
        className="hidden"
        onChange={(e) => onVideo(e.target.files && e.target.files[0])}
      />
      <input
        ref={posterRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPoster(e.target.files && e.target.files[0])}
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => videoRef.current.click()}
          disabled={!!busy}
          data-testid={`${testPrefix}-upload`}
          className="flex items-center gap-2 bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
        >
          {busy === "video" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {video && video.src ? "Replace video" : "Upload video"}
        </button>
        {video && video.src && (
          <button
            onClick={() => posterRef.current.click()}
            disabled={!!busy}
            data-testid={`${testPrefix}-poster`}
            className="flex items-center gap-2 border border-ivory/25 px-6 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:text-gold-2 disabled:opacity-60"
          >
            {busy === "poster" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Change cover image
          </button>
        )}
      </div>

      {busy === "video" && (
        <div className="mt-4 h-1 w-full max-w-sm bg-ivory/20" data-testid={`${testPrefix}-progress`}>
          <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
      )}

      {video && video.src && (
        <div className="mt-6 grid max-w-xl gap-4">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Heading</span>
            <input
              defaultValue={video.heading || headingDefault}
              onChange={(e) => setHeading(e.target.value)}
              data-testid={`${testPrefix}-heading`}
              className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Caption</span>
            <textarea
              rows="2"
              defaultValue={video.caption || ""}
              onChange={(e) => setCaption(e.target.value)}
              data-testid={`${testPrefix}-caption`}
              className="mt-2 w-full resize-none border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold"
            />
          </label>
          <button
            onClick={saveDetails}
            disabled={!!busy}
            data-testid={`${testPrefix}-save-details`}
            className="justify-self-start border border-gold/60 px-6 py-3 text-xs font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
          >
            {busy === "details" ? "Saving…" : "Save details"}
          </button>
        </div>
      )}
    </div>
  );
}
